import { CalendarClock, CalendarX2, Check } from "lucide-react";
import { Body, Card, Caption, VField, VInput, cn } from "@/components/ui";
import type { DeploymentPeriod } from "@/domains/requests/api/types";

type StepPeriodProps = {
  value: DeploymentPeriod;
  onChange: (next: DeploymentPeriod) => void;
};

export function StepPeriod({ value, onChange }: StepPeriodProps): React.JSX.Element {
  const patch = (partial: Partial<DeploymentPeriod>): void => onChange({ ...value, ...partial });

  return (
    <Card padding="lg" className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <VField
          label="8. Begindatum inzet"
          htmlFor="startDate"
          required
          hint="Vanaf wanneer kan de inzet beginnen."
        >
          <VInput
            id="startDate"
            type="date"
            value={value.startDate}
            onChange={(e) => patch({ startDate: e.target.value })}
          />
        </VField>
        <VField
          label="9. Beginuur inzet (optioneel)"
          htmlFor="deploymentStart"
          hint="Indien een specifieke startdatum/-tijd van de plaatsing gewenst is."
        >
          <VInput
            id="deploymentStart"
            type="date"
            value={value.deploymentStart ?? ""}
            onChange={(e) => patch({ deploymentStart: e.target.value })}
          />
        </VField>
        <VField
          label="10. Einddatum inzet"
          htmlFor="endDate"
          required
          hint="Vanaf wanneer mag de camera weggehaald worden."
        >
          <VInput
            id="endDate"
            type="date"
            value={value.endDate}
            onChange={(e) => patch({ endDate: e.target.value })}
          />
        </VField>
        <VField
          label="11. Verlenging mogelijk?"
          hint="Geeft de planner ruimte om de inzet te verlengen als dat nodig blijkt."
          className="md:col-span-2"
        >
          <div
            role="radiogroup"
            aria-label="Verlenging mogelijk"
            className="grid gap-3 sm:grid-cols-2"
          >
            <ExtensionOption
              icon={<CalendarClock className="size-5" aria-hidden />}
              title="Ja, verlenging bespreekbaar"
              description="Einddatum kan opschuiven indien de situatie dat vraagt."
              selected={value.extensionPossible}
              onSelect={() => patch({ extensionPossible: true })}
            />
            <ExtensionOption
              icon={<CalendarX2 className="size-5" aria-hidden />}
              title="Nee, vaste einddatum"
              description="Inzet eindigt strikt op de opgegeven einddatum."
              selected={!value.extensionPossible}
              onSelect={() => patch({ extensionPossible: false })}
            />
          </div>
        </VField>
      </div>
    </Card>
  );
}

type ExtensionOptionProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
  selected: boolean;
  onSelect: () => void;
};

function ExtensionOption({
  icon,
  title,
  description,
  selected,
  onSelect,
}: ExtensionOptionProps): React.JSX.Element {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        "group relative flex cursor-pointer items-start gap-3 rounded-lg border p-4 text-left transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
        selected
          ? "border-primary-600 bg-primary-50"
          : "border-border bg-card hover:border-primary-300 hover:bg-muted/30",
      )}
    >
      <span
        className={cn(
          "flex size-9 shrink-0 items-center justify-center rounded-md transition-colors",
          selected ? "bg-primary-600 text-on-primary" : "bg-muted text-muted-foreground",
        )}
      >
        {icon}
      </span>
      <span className="flex-1 space-y-0.5">
        <Body weight="medium" className={cn(selected && "text-primary-800")}>
          {title}
        </Body>
        <Caption tone="muted">{description}</Caption>
      </span>
      {selected ? (
        <Check className="size-4 shrink-0 text-primary-700" aria-hidden />
      ) : null}
    </button>
  );
}
